/**
 * enginePreProcess_Merge_Placeholder.ts - Placeholder processing methods for EnginePreProcess
 * Port of C# EnginePreProcess_Merge_Placeholder.cs
 * Contains processPlaceholder
 */

import { PreprocessedTemplate, TemplateOperation, TemplateOperationKind, JsonArrayPlaceholder } from '../../model/index.js';
import { JsonMergeUtil } from '../../base/engine/jsonMergeUtil.js';
import { msToNanos } from './enginePreProcess_Helper.js';
import type { EnginePreProcess } from './enginePreProcess.js';
import { JsonObject, JsonValue } from '../../app/json/jsonValue.js';
import type { TemplatePlaceholder } from '../../model/templatePlaceholder.js';

export function processPlaceholder(this: EnginePreProcess, placeholder: TemplatePlaceholder, appSite: string, currentTemplateName: string, appView: string | null, parentTemplateKey: string, operations: TemplateOperation[], recursionDepth: number, componentHtml: JsonObject, genComponentHtml: boolean, contextJson: JsonObject | null = null, flowStack: string[] | null = null, configJson: JsonObject | null = null, clientMode: boolean = false): string {
    const placeholderTimerStart = performance.now();
    try {
        const nestedOperations: TemplateOperation[] = [];

        const cleanPlaceholderName = placeholder.name.replace(/^!/, '');

        const newFlowStack = [...(flowStack || []), cleanPlaceholderName];

        let childTemplate: PreprocessedTemplate | null;
        let childJson: JsonObject | null;
        let resolvedTemplateName: string;
        let resolvedAppSite: string;
        let placeholderWrappedHtml: string;
        let placeholderLatePlaceholders: string[];

        if (placeholder.isResolved) {
            childTemplate = placeholder.resolvedTemplate as unknown as PreprocessedTemplate;
            childJson = placeholder.resolvedJson;
            resolvedTemplateName = placeholder.resolvedTemplateName!;
            resolvedAppSite = placeholder.resolvedAppSite!;
            const viewResult = this._loader!.applyViewToTemplate(appSite, currentTemplateName, placeholder.name, appView, newFlowStack, configJson, { template: childTemplate, json: childJson, resolvedTemplateName, resolvedAppSite }, clientMode);
            placeholderWrappedHtml = viewResult.viewHtml;
            placeholderLatePlaceholders = viewResult.latePlaceholders;
        } else {
            const startGetTemplate = performance.now();
            const result = this._loader!.getTemplateWithView(appSite, currentTemplateName, placeholder.name, appView, newFlowStack, configJson, clientMode);
            childTemplate = result.template;
            childJson = result.json;
            resolvedTemplateName = result.resolvedTemplateName;
            resolvedAppSite = result.resolvedAppSite;
            placeholderWrappedHtml = result.viewHtml;
            placeholderLatePlaceholders = result.latePlaceholders;
            this._getTemplateNanos += msToNanos(performance.now() - startGetTemplate);
            this._getTemplateCount++;
        }

        {
            if (placeholderWrappedHtml !== childTemplate!.originalContent) {
                const wrappedPlTemplate = new PreprocessedTemplate();
                wrappedPlTemplate.originalContent = placeholderWrappedHtml;
                wrappedPlTemplate.actualTemplateName = childTemplate!.actualTemplateName;
                wrappedPlTemplate.placeholders = childTemplate!.placeholders;
                wrappedPlTemplate.slottedTemplates = childTemplate!.slottedTemplates;
                wrappedPlTemplate.replacementMappings = childTemplate!.replacementMappings;
                wrappedPlTemplate.jsonArrayPlaceholders = childTemplate!.jsonArrayPlaceholders;
                childTemplate = wrappedPlTemplate;
            }
        }

        if (resolvedTemplateName && resolvedTemplateName !== cleanPlaceholderName && newFlowStack.length > 0) {
            newFlowStack[newFlowStack.length - 1] = resolvedTemplateName;
        }

        if (!childTemplate) {
            return placeholder.fullMatch;
        }

        const replacedWith = `${resolvedAppSite}_${resolvedTemplateName || placeholder.name}`;

        let childContent = childTemplate.originalContent;

        const isPlaceholderSkipMarked = placeholder.name.startsWith('!');
        const isSkippedOrMissing = resolvedTemplateName === this._loader!.defaultSkipTemplateName || resolvedTemplateName === this._loader!.defaultReplaceTemplateName;

        if (genComponentHtml) {
            componentHtml.set(placeholder.name, JsonValue.string(''));
        }

        if (!childContent) {
            const opType = resolvedTemplateName === this._loader!.defaultSkipTemplateName ? TemplateOperationKind.ComponentSkip : TemplateOperationKind.ComponentMissing;
            const operation = new TemplateOperation();
            operation.templateName = parentTemplateKey;
            operation.kind = opType;
            operation.target = placeholder.fullMatch;
            operation.replacedWith = replacedWith;
            operation.operations = nestedOperations;
            operations.push(operation);
            return childContent || '';
        }

        if (!isPlaceholderSkipMarked && !isSkippedOrMissing) {
            const defaultHtmlForMerge = resolvedTemplateName === this._loader!.defaultSkipTemplateName
                ? this._loader!.defaultSkipHtml
                : this._loader!.defaultReplaceHtml;

            const placeholderArrayMetadata: JsonArrayPlaceholder[] | null = (childTemplate.jsonArrayPlaceholders && childTemplate.jsonArrayPlaceholders.length > 0)
                ? childTemplate.jsonArrayPlaceholders : null;
            const placeholderJsonKeyMetadata = (childTemplate.jsonKeyPlaceholders && childTemplate.jsonKeyPlaceholders.length > 0)
                ? childTemplate.jsonKeyPlaceholders : null;
            const jsonToMerge = childJson || new JsonObject();
            const startJsonMerge = performance.now();
            childContent = JsonMergeUtil.mergeTemplateWithJson(
                childContent,
                jsonToMerge,
                replacedWith,
                resolvedTemplateName || placeholder.name,
                nestedOperations,
                this._loader!.maxArrayOperationItems,
                defaultHtmlForMerge,
                this._loader!.defaultSkipJson,
                placeholderArrayMetadata,
                placeholderJsonKeyMetadata,
                contextJson,
                configJson
            );
            this._jsonMergeNanos += msToNanos(performance.now() - startJsonMerge);
            this._jsonMergeCount++;

            childContent = this._processTemplateContent(
                childContent,
                childTemplate,
                appSite,
                cleanPlaceholderName,
                appView,
                replacedWith,
                nestedOperations,
                recursionDepth + 1,
                componentHtml,
                genComponentHtml,
                contextJson,
                newFlowStack,
                configJson,
                placeholderLatePlaceholders,
                clientMode
            );
        }

        if (genComponentHtml) {
            componentHtml.set(placeholder.name, JsonValue.string(childContent));
        }

        let finalOpType: TemplateOperationKind = TemplateOperationKind.ComponentReplace;
        if (resolvedTemplateName === this._loader!.defaultSkipTemplateName) {
            finalOpType = TemplateOperationKind.ComponentSkip;
        } else if (resolvedTemplateName === this._loader!.defaultReplaceTemplateName) {
            finalOpType = TemplateOperationKind.ComponentMissing;
        }

        const operation = new TemplateOperation();
        operation.templateName = parentTemplateKey;
        operation.kind = finalOpType;
        operation.target = placeholder.fullMatch;
        operation.replacedWith = replacedWith;
        operation.operations = nestedOperations;
        operations.push(operation);

        return childContent;
    } finally {
        this._placeholderProcessNanos += msToNanos(performance.now() - placeholderTimerStart);
        this._placeholderProcessCount++;
    }
}
